import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingBag, Menu } from 'lucide-react';
import { useCart } from '../../lib/cart';
import { useLanguage } from '../../lib/i18n';
import { LanguageSelector } from '../ui/LanguageSelector';
interface MarketplaceHeaderProps {
  showPromo?: boolean;
  /** Fuerza el modo white-label de Emilius aunque no haya slug en la URL
      ni en el carrito (p.ej. en /order-confirmation, donde el carrito ya
      se limpió). Solo lo usa OrderConfirmationPage para pedidos de Emilius. */
  forceEmilius?: boolean;
}
export function MarketplaceHeader({
  showPromo = false,
  forceEmilius = false
}: MarketplaceHeaderProps) {
  const {
    getItemCount,
    cart
  } = useCart();
  const {
    t
  } = useLanguage();
  const {
    slug: urlSlug
  } = useParams<{
    slug: string;
  }>();
  const itemCount = getItemCount();
  // Fallback to URL slug if cart doesn't have it (for legacy carts)
  const targetSlug = cart.restaurantSlug || urlSlug;
  // ── Emilius "white-label" mode ──────────────────────────────────────
  // El dueño quiere que dentro del flujo del restaurante (menú, carrito,
  // checkout, confirmación, tracking) NO existan caminos hacia el
  // marketplace de NOIZA. Detectamos por URL o por el slug guardado en
  // el carrito (cubre las páginas /cart, /checkout, /order/..., etc.
  // donde el slug no está en la URL). `forceEmilius` cubre el caso de
  // /order-confirmation, donde el carrito ya fue limpiado.
  const isEmiliusFlow = forceEmilius || urlSlug === 'emilius-pizza' || cart.restaurantSlug === 'emilius-pizza';
  if (isEmiliusFlow) {
    return <header className="sticky top-0 z-40 w-full bg-[#0B0B0B] border-b border-white/10" data-id="element-2399" data-mp="18wojth6888fu">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-2400" data-mp="145g6wb5ou4xx">
          <div className="flex justify-between items-center h-12 sm:h-16" data-id="element-2401" data-mp="143vash3q6dxy">
            {/* Emilius mark — links ONLY to the Emilius menu, never to the
                NOIZA marketplace. */}
            <Link to="/r/emilius-pizza" className="flex items-center gap-2 select-none" data-id="element-2402" data-mp="ccfpcqdwmx9b">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg overflow-hidden bg-[#F2E6D5] flex items-center justify-center shrink-0" data-id="element-2403" data-mp="2ghv5llnycqyq">
                <img src="https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png" alt="Emilius Pizza" className="w-full h-full object-contain" decoding="async" data-id="element-2404" data-mp="2laelwqe0y078" />
              </div>
              <span className="font-bold text-sm sm:text-base tracking-tight text-white" data-id="element-2405" data-mp="1xjr8jplcaida">
                Emilius Pizza
              </span>
            </Link>

            {/* Right side: powered-by label (plain text, NOT a link),
                language selector, and the customer's cart. The cart
                link stays — it goes to the user's own cart, not to
                the marketplace. */}
            <div className="flex items-center gap-2 sm:gap-4" data-id="element-2406" data-mp="3iyyfgxei7365">
              <span className="hidden sm:inline text-[10px] tracking-wide text-white/35 select-none" aria-label="Powered by noizacr.com" data-id="element-2407" data-mp="178gsw8xqbr4s">
                Powered by noizacr.com
              </span>

              <LanguageSelector data-id="element-2408" data-mp="xhcfhdnpcec2" />

              <Link to={targetSlug ? `/r/${targetSlug}/cart` : '/cart'} className="relative p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors rounded-full" data-id="element-2409" data-mp="sldvs3dbjkhz">
                <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2410" data-mp="jiuxlwkh9w22" />
                {itemCount > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] sm:text-xs font-bold leading-none text-[#0B0B0B] transform translate-x-1/4 -translate-y-1/4 bg-[#6FA248] rounded-full min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px]" data-id="element-2411" data-mp="elm9lfcnexsw">
                    {itemCount}
                  </span>}
              </Link>
            </div>
          </div>

          {/* Mobile: powered-by sits under the bar so the tiny text is
                  still visible without crowding the right-side actions. */}
          <div className="sm:hidden pb-1 -mt-0.5 text-center" data-id="element-2412" data-mp="roouqh6h23fn">
            <span className="text-[9px] tracking-wide text-white/30 select-none" data-id="element-2413" data-mp="rq9t6hvwq6is">
              Powered by noizacr.com
            </span>
          </div>
        </div>
      </header>;
  }
  // ── Default NOIZA marketplace header (unchanged for all other tenants) ──
  return <header className="sticky top-0 z-40 w-full bg-[#0B0B0B] border-b border-white/10" data-id="element-2414" data-mp="mqnc1xcyk64p">
      {/* Promo strip — only on marketplace/home */}
      {showPromo && <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-600 text-white text-center py-1 sm:py-1.5 px-2 sm:px-4" data-id="element-2415" data-mp="dvz9ieyrp2jl">
          <Link to="/r/capricho-de-canela" className="text-[11px] sm:text-sm font-bold hover:underline block truncate" data-id="element-2416" data-mp="3v5nstv6qzsd4">
            🎉 Capricho de Canela: ¡2do producto a mitad de precio! →
          </Link>
        </div>}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8" data-id="element-2417" data-mp="3chn4vr5cz004">
        <div className="flex justify-between items-center h-12 sm:h-16" data-id="element-2418" data-mp="3qgd35a3wz5q2">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-id="element-2419" data-mp="3lh9uzgsn0jdv">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#C9A34E] rounded-lg flex items-center justify-center" data-id="element-2420" data-mp="3lfp2dwpvvain">
              <span className="text-[#0B0B0B] font-bold text-base sm:text-lg leading-none" data-id="element-2421" data-mp="39u9hncka52i">
                N
              </span>
            </div>
            <span className="font-bold text-lg sm:text-xl tracking-tight text-white" data-id="element-2422" data-mp="d1zvmgg4ts9s">
              NOIZA
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-2423" data-mp="84q2oqtjayh7">
            <Link to="/" className="text-sm font-medium text-white/60 hover:text-[#C9A34E] transition-colors" data-id="element-2424" data-mp="n05xiy1xuyen">
              {t('home')}
            </Link>
            <Link to="/restaurants" className="text-sm font-medium text-white/60 hover:text-[#C9A34E] transition-colors" data-id="element-2425" data-mp="p5ccp5bzav9q">
              {t('restaurants')}
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-4" data-id="element-2426" data-mp="13sdynkr12a4o">
            <LanguageSelector data-id="element-2427" data-mp="13uqmufndpgq6" />

            <Link to={targetSlug ? `/r/${targetSlug}/cart` : '/cart'} className="relative p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors rounded-full" data-id="element-2428" data-mp="18s0jaf96qsni">
              <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-2429" data-mp="1hrx58946xdev" />
              {itemCount > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] sm:text-xs font-bold leading-none text-[#0B0B0B] transform translate-x-1/4 -translate-y-1/4 bg-[#C9A34E] rounded-full min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-[20px]" data-id="element-2430" data-mp="1ms1jrtljfy2g">
                  {itemCount}
                </span>}
            </Link>

            <button className="md:hidden p-1.5 text-white/60 hover:text-white rounded-full" data-id="element-2431" data-mp="3c8nfw5smkeql">
              <Menu className="w-5 h-5" data-id="element-2432" data-mp="379k7cbdfrqdv" />
            </button>
          </div>
        </div>
      </div>
    </header>;
}